import React, { useEffect, useState } from 'react';
import { BellAlertIcon, XMarkIcon, ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import { usePushNotifications } from '../../contexts/PushNotificationContext';
import { useToast } from '../../contexts/ToastContext';

/** Elutasítás után ennyi ideig nem kérdezünk újra. */
const SNOOZE_DAYS = 7;
const SNOOZE_KEY = 'clubtracker:push-prompt-snoozed-until';

const isSnoozed = (): boolean => {
  const until = localStorage.getItem(SNOOZE_KEY);
  return until !== null && Number(until) > Date.now();
};

const snooze = () => {
  localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
};

/**
 * Finom rákérdezés az értesítések bekapcsolására, bejelentkezés után.
 *
 * Szándékosan NEM hívjuk automatikusan a böngésző engedélykérőjét: a Firefox és
 * a Safari csak felhasználói kattintásból engedi, ráadásul egy elutasított
 * engedélyt később már nem lehet újra kérni. Ezért előbb a saját sávunk kérdez,
 * és csak a "Bekapcsolom" gomb hívja a böngésző párbeszédét.
 */
const PushPermissionPrompt: React.FC = () => {
  const {
    supported,
    needsHomeScreenInstall,
    subscribed,
    permission,
    unavailableReason,
    loading,
    busy,
    enable,
  } = usePushNotifications();
  const { showToast } = useToast();

  const [dismissed, setDismissed] = useState(() => isSnoozed());
  const [visible, setVisible] = useState(false);

  // iPhone-on csak a kezdőképernyőre tett appból működik, ott telepítésre biztatunk
  const showInstallHint = needsHomeScreenInstall;

  const shouldAsk =
    !dismissed &&
    !loading &&
    !subscribed &&
    (showInstallHint || (supported && permission === 'default' && unavailableReason === null));

  // Kis késleltetés, hogy ne a betöltés pillanatában ugorjon be
  useEffect(() => {
    if (!shouldAsk) {
      setVisible(false);
      return;
    }

    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [shouldAsk]);

  if (!shouldAsk) {
    return null;
  }

  const handleDismiss = () => {
    snooze();
    setDismissed(true);
  };

  const handleEnable = async () => {
    try {
      await enable();
      showToast('Értesítések bekapcsolva.');
      setDismissed(true);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Nem sikerült bekapcsolni az értesítéseket.',
        'error'
      );
      // Elutasított engedélyt nem lehet újra kérni, ne kérdezzünk tovább
      handleDismiss();
    }
  };

  return (
    <div
      className={`fixed inset-x-0 bottom-16 z-50 px-4 pb-safe-1 transition-all duration-300 lg:bottom-4 lg:left-auto lg:right-4 lg:inset-x-auto lg:w-96 lg:px-0 ${
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
      }`}
      role="region"
      aria-label="Értesítések bekapcsolása"
    >
      <div className="rounded-xl border border-gray-200 bg-white p-4 text-left shadow-lg">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary-100 p-2">
            {showInstallHint ? (
              <ArrowUpOnSquareIcon className="h-5 w-5 text-primary-600" />
            ) : (
              <BellAlertIcon className="h-5 w-5 text-primary-600" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">
              {showInstallHint ? 'Tedd ki az appot a kezdőképernyőre' : 'Ne maradj le semmiről'}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {showInstallHint
                ? 'iPhone-on az értesítések csak a kezdőképernyőre tett appból működnek. Koppints a Megosztás gombra, majd a „Főképernyőhöz adás” lehetőségre.'
                : 'Kapcsold be az értesítéseket, és szólunk az edzésekről, a változásokról és az elmaradó eseményekről - akkor is, ha az app nincs megnyitva.'}
            </p>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Bezárás"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!showInstallHint && (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleEnable}
              disabled={busy}
              className="flex-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? 'Bekapcsolás...' : 'Bekapcsolom'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Most nem
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushPermissionPrompt;
